// ToastProvider — the storefront's transient notices.
//
// For messages that are ABOUT an action just taken (a send that failed, a copy
// that succeeded) rather than about the form's persistent state: they appear
// over the page, say their piece, and leave — instead of pushing the layout
// around as inline alert boxes.
//
// Deliberately small: no queue cap games, no imperative singleton — a context
// hook (`useToast`) and a fixed stack at the bottom of the viewport. Auto-
// dismisses after 6s; a close button for sooner. `role` follows severity so a
// screen reader hears a failure assertively and an info line politely.
//
// No `@fluxora/ui` Toast exists yet — when a second app needs this, promote it
// (the promotion path in CLAUDE.md), don't copy it.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface ToastInput {
  readonly title: string;
  readonly body?: string;
  /** `danger` announces assertively; everything else is polite. */
  readonly variant?: 'default' | 'danger' | 'warning' | 'success';
}

interface ToastRecord extends ToastInput {
  readonly id: number;
}

interface ToastContextValue {
  readonly showToast: (toast: ToastInput) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const AUTO_DISMISS_MS = 6_000;

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (ctx === null) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
}

const VARIANT_BAR: Record<NonNullable<ToastInput['variant']>, string> = {
  default: 'bg-primary',
  danger: 'bg-danger',
  warning: 'bg-warning',
  success: 'bg-success',
};

function ToastCard({
  toast,
  onClose,
}: {
  readonly toast: ToastRecord;
  readonly onClose: () => void;
}): JSX.Element {
  useEffect(() => {
    const timer = setTimeout(onClose, AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [onClose]);

  const variant = toast.variant ?? 'default';
  return (
    <div
      role={variant === 'danger' ? 'alert' : 'status'}
      className="toast-in pointer-events-auto flex w-full max-w-sm items-start gap-3 overflow-hidden rounded-[14px] border border-border bg-surface p-3 pr-2 shadow-float"
    >
      <span aria-hidden="true" className={`mt-0.5 h-full w-1 shrink-0 self-stretch rounded-full ${VARIANT_BAR[variant]}`} />
      <div className="flex min-w-0 flex-1 flex-col gap-0.5 py-0.5">
        <span className="text-sm font-semibold text-foreground">{toast.title}</span>
        {toast.body !== undefined ? (
          <span className="text-xs leading-relaxed text-foreground/70">{toast.body}</span>
        ) : null}
      </div>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={onClose}
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-foreground/50 transition-colors hover:bg-border/60 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-3.5 w-3.5">
          <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

export function ToastProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<readonly ToastRecord[]>([]);
  const nextId = useRef(1);

  const showToast = useCallback((toast: ToastInput): void => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((prev) => [...prev, { ...toast, id }]);
  }, []);

  const dismiss = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        className="pointer-events-none fixed inset-x-0 bottom-6 z-[1600] flex flex-col items-center gap-2 px-4"
      >
        {toasts.map((toast) => (
          <ToastCard key={toast.id} toast={toast} onClose={() => dismiss(toast.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
